import { round } from "lodash";
import { FuelSource } from "./chooseFuelCounter";
import { CounterUsage } from "./counterUsage";
import { EnergyDataIssue, EnergyDataStatus, energyDataStatus } from "./energyDataQuality";

/**
 * Below this the uncovered distance is treated as the rounding noise it usually is: a counter
 * reports a few minutes after a trip starts, which leaves a kilometre or two outside its window
 * on nearly every vehicle.
 */
const UNCOVERED_MIN_KM = 5;
/** Share of the period distance outside the measured window above which the figure is flagged. */
const UNCOVERED_RELATIVE_THRESHOLD = 0.05;
/** Below this, a fuel deviation is rounding and timing noise rather than something to look into. */
const FUEL_DEVIATION_MIN_LITERS = 20;
/** Deviation relative to the larger of the two figures, above which it is flagged. */
const FUEL_DEVIATION_RELATIVE_THRESHOLD = 0.2;

/** A trip reduced to what the consumption figures need from it. */
export interface TripDistance {
  start: Date;
  stop: Date;
  /** Distance in kilometres, as Geotab reports it on `Trip`. */
  distance: number;
}

export interface ConsumptionInput {
  deviceId: string;
  name: string;
  licensePlate: string;
  trips: TripDistance[];
  fuel: CounterUsage;
  /** Which of the two fuel counters `fuel` came from, see `chooseFuelCounter`. */
  fuelSource: FuelSource | null;
  electric: CounterUsage;
  /** Energy taken from chargers in the period, summed over the charge events. */
  chargedKWh: number | null;
  /** Time spent charging in the period, summed over the charge events. */
  chargeDurationMs: number | null;
  /** Volume of the fill-ups in the period, or `null` when none were recorded. */
  filledLiters: number | null;
  /** Fill-ups that were detected but whose volume Geotab could not derive. */
  fillsWithoutVolume: number;
  /** Whether one of the lists for this vehicle came back at its results limit. */
  truncated: boolean;
  /** Whether the request for this vehicle failed. */
  failed: boolean;
}

export interface ConsumptionRow {
  id: string;
  name: string;
  licensePlate: string;
  fuelUsedLiters: number | null;
  /** Which of the two fuel counters the figure came from, so the reader can tell. */
  fuelSource: FuelSource | null;
  electricUsedKWh: number | null;
  filledLiters: number | null;
  chargedKWh: number | null;
  chargeDurationHours: number | null;
  /** Distance over the whole period asked for. */
  distanceKm: number | null;
  /** Distance driven inside the stretch the counters reported for. The rates are based on this. */
  measuredDistanceKm: number | null;
  /** Distance driven outside that stretch, and therefore not covered by the consumption figures. */
  uncoveredDistanceKm: number;
  fuelRatePer100Km: number | null;
  electricRatePer100Km: number | null;
  /** The stretch the counters reported for, which is usually shorter than the period asked for. */
  measuredFrom: Date | null;
  measuredTo: Date | null;
  /**
   * The window of the counter that covers the period least well, which is the one
   * `uncoveredDistanceKm` refers to. On a vehicle with a single counter this is that counter.
   */
  coverageFrom: Date | null;
  coverageTo: Date | null;
  resetCount: number;
  /** Fills minus measured consumption. Positive means more was tanked than the counter saw. */
  fuelDeviationLiters: number | null;
  fuelDeviationFlagged: boolean;
  /** Fuel was burned per the counter but no fill-up was recorded in the period. */
  noFillsRecorded: boolean;
  /** At least one detected fill-up had no derivable volume, so the total is short. */
  fillVolumeMissing: boolean;
  issues: EnergyDataIssue[];
  status: EnergyDataStatus;
}

/**
 * Builds one vehicle row for the consumption view.
 *
 * The point of interest is the measured window. Geotab answers with whatever the vehicle
 * reported, which is regularly only part of the period asked for; dividing a partial consumption
 * by the full period distance then understates the rate, with nothing on screen to show it. So
 * the rates are computed against the distance driven inside the window the counters actually
 * covered, and the distance left outside it is carried on the row so it can be shown.
 *
 * @param input - See {@link ConsumptionInput}.
 * @returns The row, including what is incomplete about it.
 */
export function buildConsumptionRow(input: ConsumptionInput): ConsumptionRow {
  const { fuel, electric } = input;

  const distanceKm = sumDistance(input.trips);
  const fuelUsedLiters = fuel.used;
  const electricUsedKWh = electric.used;
  const hasConsumption = fuelUsedLiters != null || electricUsedKWh != null;

  // Per counter, not for the two together. A plug-in hybrid whose fuel counter reports all month
  // and whose electric counter reports only the last day would otherwise divide one day of kWh by
  // a month of kilometres — out by a factor of thirty, and with nothing flagged, because between
  // them the two counters do cover the period.
  const fuelCoverage = coverageOf(fuel, input.trips, distanceKm);
  const electricCoverage = coverageOf(electric, input.trips, distanceKm);

  // Only the counters that produced a figure can leave one incomplete.
  const reporting = [
    ...(fuelUsedLiters != null ? [fuelCoverage] : []),
    ...(electricUsedKWh != null ? [electricCoverage] : []),
  ];
  const worst = reporting.reduce<Coverage | null>(
    (limiting, coverage) =>
      !limiting || coverage.uncoveredDistanceKm > limiting.uncoveredDistanceKm
        ? coverage
        : limiting,
    null,
  );

  const unionFrom = earliest(fuel.measuredFrom, electric.measuredFrom);
  const unionTo = latest(fuel.measuredTo, electric.measuredTo);
  const unionDistanceKm = distanceWithin(input.trips, unionFrom, unionTo);

  const { deviationLiters, flagged } = fuelDeviation(fuelUsedLiters, input.filledLiters);

  const issues: EnergyDataIssue[] = [];
  if (input.failed) {
    issues.push("requestFailed");
  } else {
    if (fuel.sampleCount === 0 && electric.sampleCount === 0) {
      issues.push("noReadings");
    } else if (!hasConsumption) {
      issues.push("singleReading");
    }
    if (fuel.resetCount + electric.resetCount > 0) {
      issues.push("counterReset");
    }
    // Only worth asking how well the window is covered once there is a figure to qualify.
    // Without one, "the period is only partly covered" says nothing beyond "nothing was
    // reported", and two issues where one belongs make the tooltip harder to act on.
    if (reporting.length > 0) {
      if (reporting.every(coverage => coverage.measuredDistanceKm === 0)) {
        issues.push("noDistance");
      } else if (worst && isUncovered(worst.uncoveredDistanceKm, distanceKm)) {
        issues.push("partialCoverage");
      }
    }
    if (input.truncated) {
      issues.push("truncated");
    }
  }

  return {
    id: input.deviceId,
    name: input.name,
    licensePlate: input.licensePlate,
    fuelUsedLiters,
    fuelSource: input.fuelSource,
    electricUsedKWh,
    filledLiters: input.filledLiters,
    chargedKWh: input.chargedKWh,
    chargeDurationHours:
      input.chargeDurationMs != null ? round(input.chargeDurationMs / 3_600_000, 2) : null,
    distanceKm: distanceKm > 0 ? distanceKm : null,
    measuredDistanceKm: unionDistanceKm > 0 ? unionDistanceKm : null,
    uncoveredDistanceKm: worst?.uncoveredDistanceKm ?? 0,
    fuelRatePer100Km: ratePer100Km(fuelUsedLiters, fuelCoverage.measuredDistanceKm),
    electricRatePer100Km: ratePer100Km(electricUsedKWh, electricCoverage.measuredDistanceKm),
    measuredFrom: unionFrom,
    measuredTo: unionTo,
    coverageFrom: worst?.from ?? null,
    coverageTo: worst?.to ?? null,
    resetCount: fuel.resetCount + electric.resetCount,
    fuelDeviationLiters: deviationLiters,
    fuelDeviationFlagged: flagged,
    noFillsRecorded:
      fuelUsedLiters != null &&
      fuelUsedLiters > 0 &&
      input.filledLiters == null &&
      input.fillsWithoutVolume === 0,
    fillVolumeMissing: input.fillsWithoutVolume > 0,
    issues,
    status: energyDataStatus(issues),
  };
}

/** How much of the period distance one counter has readings for. */
interface Coverage {
  from: Date | null;
  to: Date | null;
  measuredDistanceKm: number;
  uncoveredDistanceKm: number;
}

function coverageOf(usage: CounterUsage, trips: TripDistance[], distanceKm: number): Coverage {
  const measuredDistanceKm = distanceWithin(trips, usage.measuredFrom, usage.measuredTo);

  return {
    from: usage.measuredFrom,
    to: usage.measuredTo,
    measuredDistanceKm,
    uncoveredDistanceKm: Math.max(0, distanceKm - measuredDistanceKm),
  };
}

/**
 * Distance driven inside `[from, to]`, counting a trip that only partly overlaps the window by
 * its overlapping share rather than all-or-nothing on its start time. A trip is assumed to cover
 * its distance evenly over its own duration, so the share is the overlapping time divided by the
 * trip's total time; an instantaneous trip (start equals stop, as Geotab sometimes reports) counts
 * in full when that instant falls inside the window, and not at all otherwise.
 */
function distanceWithin(trips: TripDistance[], from: Date | null, to: Date | null) {
  if (!from || !to) {
    return 0;
  }
  const fromMs = from.getTime();
  const toMs = to.getTime();

  return trips.reduce((sum, trip) => {
    if (!Number.isFinite(trip.distance)) {
      return sum;
    }
    const startMs = trip.start.getTime();
    const stopMs = trip.stop.getTime();
    const durationMs = stopMs - startMs;

    if (durationMs <= 0) {
      // An instantaneous (or malformed) trip has no span to overlap the window with, so it
      // counts in full when that single instant falls inside the window, and not at all
      // otherwise.
      return startMs >= fromMs && startMs <= toMs ? sum + trip.distance : sum;
    }

    const overlapMs = Math.min(stopMs, toMs) - Math.max(startMs, fromMs);
    if (overlapMs <= 0) {
      return sum;
    }
    return sum + trip.distance * Math.min(1, overlapMs / durationMs);
  }, 0);
}

/**
 * Cross-checks the tanked volume against what the engine-side counter says was burned. Only the
 * positive direction is flagged: running on fuel tanked before the period is ordinary, whereas
 * tanking far more than was burned points at a broken or missing sensor, or at fuel going
 * somewhere other than the tank.
 *
 * A deviation is only meaningful when both sides produced a figure. One-sided data has its own
 * hints: `noReadings` covers the counter side, `noFillsRecorded` covers the fill-up side.
 */
function fuelDeviation(usedLiters: number | null, filledLiters: number | null) {
  if (usedLiters == null || filledLiters == null) {
    return { deviationLiters: null, flagged: false };
  }

  const deviationLiters = filledLiters - usedLiters;
  const reference = Math.max(filledLiters, usedLiters, 1);
  const flagged =
    deviationLiters >= FUEL_DEVIATION_MIN_LITERS &&
    deviationLiters / reference >= FUEL_DEVIATION_RELATIVE_THRESHOLD;

  return { deviationLiters, flagged };
}

function isUncovered(uncoveredKm: number, distanceKm: number) {
  return (
    uncoveredKm >= UNCOVERED_MIN_KM &&
    distanceKm > 0 &&
    uncoveredKm / distanceKm >= UNCOVERED_RELATIVE_THRESHOLD
  );
}

function ratePer100Km(used: number | null, distanceKm: number) {
  if (used == null || distanceKm <= 0) {
    return null;
  }
  return (used / distanceKm) * 100;
}

function sumDistance(trips: TripDistance[]) {
  return trips.reduce((sum, trip) => sum + (Number.isFinite(trip.distance) ? trip.distance : 0), 0);
}

function earliest(first: Date | null, second: Date | null) {
  if (!first) return second;
  if (!second) return first;
  return first.getTime() <= second.getTime() ? first : second;
}

function latest(first: Date | null, second: Date | null) {
  if (!first) return second;
  if (!second) return first;
  return first.getTime() >= second.getTime() ? first : second;
}

